import type { ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { GHOST_FOCUS_RING } from '../ui-chrome';
import { InfoIcon } from './settings-icons';

interface SettingsFieldCardProps {
	title: string;
	label: string;
	/** The input the label names. Without one the label is plain text, for a group of inputs. */
	labelFor?: string;
	/** The info tooltip and its button's name. Without them the label stands alone. */
	infoAlt?: string;
	info?: string;
	children: ReactNode;
}

const LABEL = 'text-xs leading-4 font-medium text-gray-900';

/** A titled white card on the Settings tab: a label, with an info tooltip if given, then its fields. */
export const SettingsFieldCard = ({ title, label, labelFor, infoAlt, info, children }: SettingsFieldCardProps) => (
	<section className='flex w-full flex-col gap-3'>
		<h2 className='text-[13px] leading-[18px] font-medium text-gray-800'>{title}</h2>
		<div className='flex w-full flex-col rounded-[20px] border border-gray-500 bg-white px-3 pt-4 pb-3'>
			<div className='flex w-full flex-col gap-2'>
				<div className='flex items-center gap-1'>
					{labelFor ? (
						<label htmlFor={labelFor} className={LABEL}>
							{label}
						</label>
					) : (
						<p className={LABEL}>{label}</p>
					)}
					{info && (
						<Tooltip>
							<TooltipTrigger asChild>
								{/* A button so the tip is reachable by keyboard; sized to the 12px icon, as in Figma. */}
								<Button
									type='button'
									variant='ghost'
									size='icon-sm'
									aria-label={infoAlt}
									className={cn('size-3 rounded-full hover:bg-transparent', GHOST_FOCUS_RING)}
								>
									<InfoIcon className='size-3 text-gray-800' />
								</Button>
							</TooltipTrigger>
							<TooltipContent>{info}</TooltipContent>
						</Tooltip>
					)}
				</div>
				{children}
			</div>
		</div>
	</section>
);
